import React, { useState } from 'react'

import { selectData } from 'layouts/onboarding'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import FormField from 'components/FormField'
import ValuePoint from 'admin/components/ValuePoint'
import FamilyDependents from 'admin/components/FamilyDependents'
import { Card, Grid, Typography, Collapse } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'

import {
  format,
  differenceInYears,
  differenceInMonths,
  addMonths,
  subYears,
} from 'date-fns'

const dateToYears = (date) =>
  differenceInYears(new Date(), new Date(date || new Date()))
const yearsToDate = (years) => format(subYears(new Date(), years), 'yyyy-MM-dd')

const dollarCentsFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function Family({
  collapseSections,
  toggleCollapse,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
  formerSpouseOwedPensionPercentage = 0,
  atRetirementGrossPension = 0,
}) {
  const isGovernmentEmployee = intakeState.governmentEmployee
  const [localForm, setLocalForm] = useState({
    familySpouseFirstName: intakeState.familySpouseFirstName || '',
    familySpouseLastName: intakeState.familySpouseLastName || '',
    familySpousePhone: intakeState.familySpousePhone || '',
    familySpouseDateOfBirth: intakeState.familySpouseDateOfBirth || '',
    familySpouseAge: dateToYears(intakeState?.familySpouseDateOfBirth) || '',
    familyDivorcedMarriageStartDate:
      intakeState.familyDivorcedMarriageStartDate || '',
    familyDivorcedMarriageEndDate:
      intakeState.familyDivorcedMarriageEndDate || '',
    familyChildren: intakeState.familyChildren || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  const maritalStatus = intakeState?.familyMaritalStatus
  const divorcedStatus = intakeState?.familyDivorced
  const divorcedOwed = intakeState?.familyDivorcedOwedPension

  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        <Card
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="valueTitle">VALUE POINTS</Typography>
              <Collapse in={collapseSections.family} style={{ width: '100%' }}>
                {intakeState.familyDivorcedOwedPension && (
                  <>
                    <ValuePoint
                      text={`Will owe Former Spouse Up to ${
                        Math.floor(formerSpouseOwedPensionPercentage * 100) /
                        100
                      }% or ${dollarCentsFormat.format(
                        (atRetirementGrossPension *
                          (formerSpouseOwedPensionPercentage / 100)) /
                          12
                      )} of Monthly Pension`}
                      type="warning"
                    />
                  </>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="family"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse()}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Family
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.family
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse in={collapseSections.family} style={{ width: '100%' }}>
            <Grid container columnSpacing={3} rowSpacing={3} pb={2}>
              <Grid item xs={12} xl={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'familyMaritalStatus',
                  label: 'What is your current Marital Status?',
                  placeholder: intakeState.familyMaritalStatus,
                  inputType: 'select',
                  value: intakeState.familyMaritalStatus,
                  onChange: updateIntakeStateAndSubmit((familyMaritalStatus) =>
                    updateIntakeStateAtKey({
                      familyMaritalStatus: familyMaritalStatus,
                      familyDivorced:
                        familyMaritalStatus === 'divorced' ? true : false,
                    })
                  ),
                  options: selectData.relationshipStatus,
                })}
              </Grid>

              {maritalStatus === 'married' && (
                <>
                  <Grid item xs={0} xl={6} p={'0px !important'}></Grid>
                  <Grid item xs={12} xl={6}>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        {renderMuiFormField({
                          id: 'familySpouseFirstName',
                          label: "Spouse's First Name",
                          placeholder: "Spouse's First Name",
                          inputType: 'text',
                          value: localForm.familySpouseFirstName,
                          onChange: (e) =>
                            handleChange('familySpouseFirstName')(e),
                          onBlur: updateIntakeStateAndSubmit(
                            (familySpouseFirstName) =>
                              updateIntakeStateAtKey({
                                familySpouseFirstName,
                              })
                          ),
                        })}
                      </Grid>
                      <Grid item xs={6}>
                        {renderMuiFormField({
                          id: 'familySpouseLastName',
                          label: "Spouse's Last Name",
                          placeholder: "Spouse's Last Name",
                          inputType: 'text',
                          value: localForm.familySpouseLastName,
                          onChange: (e) =>
                            handleChange('familySpouseLastName')(e),
                          onBlur: updateIntakeStateAndSubmit(
                            (familySpouseLastName) =>
                              updateIntakeStateAtKey({
                                familySpouseLastName,
                              })
                          ),
                        })}
                      </Grid>
                      <Grid item xs={8}>
                        {renderMuiFormField({
                          id: 'familySpouseDateOfBirth',
                          label: `Spouse's Date of Birth`,
                          inputType: 'date',
                          defaultDate: '1970-01-01',
                          dateListTrim: { start: 0, end: 80 },
                          value: localForm.familySpouseDateOfBirth,
                          onChange: (e) => {
                            handleChange('familySpouseDateOfBirth')(e)
                            handleChange('familySpouseAge')(dateToYears(e))
                          },
                          onBlur: updateIntakeStateAndSubmit(
                            (familySpouseDateOfBirth) =>
                              updateIntakeStateAtKey({
                                familySpouseDateOfBirth,
                              })
                          ),
                        })}
                      </Grid>
                      <Grid item xs={4}>
                        {renderMuiFormField({
                          id: 'familySpouseAge',
                          label: `Spouse's Age`,
                          inputType: 'int',
                          placeholder: 'e.g. 62',
                          value: localForm.familySpouseAge,
                          onChange: (e) => {
                            handleChange('familySpouseAge')(e)
                            handleChange('familySpouseDateOfBirth')(
                              yearsToDate(e)
                            )
                          },
                          onBlur: updateIntakeStateAndSubmit(
                            (familySpouseDateOfBirth) =>
                              updateIntakeStateAtKey({
                                familySpouseDateOfBirth: yearsToDate(
                                  familySpouseDateOfBirth
                                ),
                              })
                          ),
                        })}
                      </Grid>
                      <Grid item xs={12}>
                        {renderMuiFormField({
                          id: 'familySpousePhone',
                          label: "Spouse's Phone #",
                          placeholder: "Spouse's #",
                          inputType: 'tel',
                          value: localForm.familySpousePhone,
                          onChange: (e) => handleChange('familySpousePhone')(e),
                          onBlur: updateIntakeStateAndSubmit(
                            (familySpousePhone) =>
                              updateIntakeStateAtKey({
                                familySpousePhone,
                              })
                          ),
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
              <Grid item xs={0} xl={6}></Grid>
              <Grid item xs={12} xl={6}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'familyDivorced',
                  label: 'Have you ever been divorced?',
                  placeholder: intakeState.familyDivorced,
                  inputType: 'select',
                  value: intakeState.familyDivorced,
                  onChange: updateIntakeStateAndSubmit((familyDivorced) =>
                    updateIntakeStateAtKey({ familyDivorced })
                  ),
                  options: selectData.yesNo,
                })}
              </Grid>

              {divorcedStatus && (
                <>
                  <Grid item xs={0} xl={6} p={'0px !important'}></Grid>
                  {isGovernmentEmployee && (
                    <Grid item xs={12} xl={6}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'familyDivorcedOwedPension',
                        label:
                          'Will your former spouse be owed any of your pension?',
                        placeholder: intakeState.familyDivorcedOwedPension,
                        inputType: 'select',
                        value: intakeState.familyDivorcedOwedPension,
                        onChange: updateIntakeStateAndSubmit(
                          (familyDivorcedOwedPension) =>
                            updateIntakeStateAtKey({
                              familyDivorcedOwedPension,
                            })
                        ),
                        options: selectData.yesNo,
                      })}
                    </Grid>
                  )}

                  {divorcedOwed && (
                    <>
                      <Grid item xs={0} xl={6} p={'0px !important'}></Grid>
                      <Grid item xs={12} xl={6}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            {renderMuiFormField({
                              id: 'familyDivorcedMarriageStartDate',
                              label: 'Marriage date with former spouse',
                              inputType: 'date',
                              defaultDate: '1970-01-01',
                              dateListTrim: { start: 0, end: 80 },
                              value: localForm.familyDivorcedMarriageStartDate,
                              onChange: (e) =>
                                handleChange('familyDivorcedMarriageStartDate')(
                                  e
                                ),
                              onBlur: updateIntakeStateAndSubmit(
                                (familyDivorcedMarriageStartDate) =>
                                  updateIntakeStateAtKey({
                                    familyDivorcedMarriageStartDate,
                                  })
                              ),
                            })}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {renderMuiFormField({
                              id: 'familyDivorcedMarriageEndDate',
                              label: 'Divorce date with former spouse',
                              inputType: 'date',
                              defaultDate: '1970-01-01',
                              dateListTrim: { start: 0, end: 80 },
                              value: localForm.familyDivorcedMarriageEndDate,
                              onChange: (e) =>
                                handleChange('familyDivorcedMarriageEndDate')(
                                  e
                                ),
                              onBlur: updateIntakeStateAndSubmit(
                                (familyDivorcedMarriageEndDate) =>
                                  updateIntakeStateAtKey({
                                    familyDivorcedMarriageEndDate,
                                  })
                              ),
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </>
              )}
              <Grid item xs={0}></Grid>
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'familyChildren',
                      label: 'Number of children (if any)',
                      placeholder: intakeState.familyChildren,
                      inputType: 'int',
                      maxValue: 12,
                      value: localForm.familyChildren,
                      onChange: (e) => handleChange('familyChildren')(e),
                      onBlur: updateIntakeStateAndSubmit((familyChildren) =>
                        updateIntakeStateAtKey({ familyChildren })
                      ),
                    })}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FamilyDependents
                  numDependents={intakeState?.familyChildren || 0}
                  initialDependents={intakeState?.familyDependents || []}
                  updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
                  updateIntakeStateAtKey={updateIntakeStateAtKey}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default Family
